import { useMemo, useState } from 'react'
import {
  UserPlus,
  Users,
  UserMinus,
  RefreshCw,
  Filter,
  Search,
  X,
  User,
  Mail,
  Briefcase,
  UserCircle,
  Building2,
  ArrowRight,
} from 'lucide-react'
import { useTimes } from '../hooks/useTime'
import { usePessoas, useUpdatePessoa } from '../../pessoas/hooks/usePessoa'
import { Pessoa } from '../../pessoas/tipos/pessoa'
import { Time } from '../tipos/time'

function pessoaToFormData(p: Pessoa, timeId: string) {
  return {
    idPessoa: p.idPessoa,
    nome: p.nome,
    email: p.email,
    telefone: p.telefone ?? '',
    observacao: p.observacao ?? '',
    cargoCodigo: p.cargoCodigo != null ? String(p.cargoCodigo) : '',
    ativo: p.ativo,
    timeId,
  }
}

function filtrarPorTermo(pessoas: Pessoa[], termo: string): Pessoa[] {
  if (!termo.trim()) return []
  const t = termo.trim().toLowerCase()
  return pessoas.filter(
    (p) =>
      p.nome.toLowerCase().includes(t) ||
      (p.email && p.email.toLowerCase().includes(t))
  )
}

function getNomeTime(timeId: number | undefined, times: Time[]): string {
  if (timeId == null) return ''
  const time = times.find((t) => t.id === timeId)
  return time?.descricao ?? ''
}

function CelulaTime({ timeId, times }: { timeId: number | undefined; times: Time[] }) {
  const nome = getNomeTime(timeId, times)
  const temTime = nome.length > 0
  return (
    <span className="inline-flex items-center gap-2 text-sm">
      {temTime ? (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-brand-50 text-brand-700 font-medium border border-brand-100">
          <Building2 className="w-4 h-4 flex-shrink-0" />
          {nome}
        </span>
      ) : (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gray-100 text-gray-500 font-medium">
          <Building2 className="w-4 h-4 flex-shrink-0" />
          Sem time
        </span>
      )}
    </span>
  )
}

export default function PessoasTimes() {
  const [filtroTimeId, setFiltroTimeId] = useState<string>('')
  const [filtroPessoa, setFiltroPessoa] = useState<string>('')
  const [pessoaIdParaAdicionar, setPessoaIdParaAdicionar] = useState<string>('')

  const timeIdSelecionado = filtroTimeId === '' ? null : Number(filtroTimeId)

  const { data: times = [], isLoading: loadingTimes } = useTimes({})
  const { data: pessoasNoTime = [], isLoading: loadingPessoasNoTime, refetch: refetchNoTime } = usePessoas(
    timeIdSelecionado != null ? { timeId: String(timeIdSelecionado) } : undefined
  )
  const { data: todasPessoas = [] } = usePessoas({})

  const updateMutation = useUpdatePessoa()

  const resultadoBuscaPessoa = useMemo(
    () => filtrarPorTermo(todasPessoas, filtroPessoa),
    [todasPessoas, filtroPessoa]
  )

  const pessoasDisponiveisParaAdicionar = useMemo(() => {
    if (timeIdSelecionado == null) return []
    const fora = todasPessoas.filter((p) => p.timeId !== timeIdSelecionado)
    if (!filtroPessoa.trim()) return fora
    return filtrarPorTermo(fora, filtroPessoa).length ? filtrarPorTermo(fora, filtroPessoa) : fora
  }, [todasPessoas, timeIdSelecionado, filtroPessoa])

  const membrosExibidos = useMemo(() => {
    if (!timeIdSelecionado) return []
    if (!filtroPessoa.trim()) return pessoasNoTime
    return filtrarPorTermo(pessoasNoTime, filtroPessoa)
  }, [pessoasNoTime, filtroPessoa, timeIdSelecionado])

  const timeSelecionado = useMemo(
    () => times.find((t) => t.id === timeIdSelecionado),
    [times, timeIdSelecionado]
  )

  const handleLimparFiltros = () => {
    setFiltroTimeId('')
    setFiltroPessoa('')
    setPessoaIdParaAdicionar('')
  }

  const handleRemoverDoTime = (pessoa: Pessoa) => {
    if (!pessoa.idPessoa || timeIdSelecionado == null) return
    const data = pessoaToFormData(pessoa, '')
    updateMutation.mutate(
      { id: pessoa.idPessoa, data },
      { onSuccess: () => refetchNoTime() }
    )
  }

  const handleAdicionarAoTime = () => {
    if (!pessoaIdParaAdicionar || timeIdSelecionado == null) return
    const id = Number(pessoaIdParaAdicionar)
    const pessoa = todasPessoas.find((p) => p.idPessoa === id)
    if (!pessoa) return
    const data = pessoaToFormData(pessoa, String(timeIdSelecionado))
    updateMutation.mutate(
      { id, data },
      {
        onSuccess: () => {
          setPessoaIdParaAdicionar('')
          refetchNoTime()
        },
      }
    )
  }

  const handleBuscar = (e: React.FormEvent) => {
    e.preventDefault()
  }

  const temBuscaPorPessoa = filtroPessoa.trim().length > 0

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-r from-brand-500 to-brand-600 rounded-xl flex items-center justify-center flex-shrink-0">
            <UserPlus className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Pessoas e Times</h1>
            <p className="mt-1 text-sm text-gray-600">
              Gerencie a relação entre pessoas e times
            </p>
          </div>
        </div>
      </div>

      {/* Card de Filtros */}
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-theme-sm mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-brand-500 to-brand-600 rounded-lg flex items-center justify-center">
              <Filter className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Filtros de Pessoas e Times</h3>
              <p className="text-sm text-gray-600">Busque por time ou por pessoa (nome/e-mail)</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleLimparFiltros}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all duration-200 font-medium"
          >
            <X className="w-4 h-4" />
            Limpar
          </button>
        </div>

        <form onSubmit={handleBuscar} className="flex flex-wrap items-end gap-4">
          <div className="min-w-[200px] flex-1">
            <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Users className="w-4 h-4 text-brand-500" />
              Time
            </label>
            <select
              value={filtroTimeId}
              onChange={(e) => {
                setFiltroTimeId(e.target.value)
                setPessoaIdParaAdicionar('')
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">Todos / Selecione um time</option>
              {loadingTimes ? (
                <option disabled>Carregando...</option>
              ) : (
                times.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.descricao}
                  </option>
                ))
              )}
            </select>
          </div>
          <div className="min-w-[200px] flex-1">
            <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <User className="w-4 h-4 text-brand-500" />
              Buscar por pessoa
            </label>
            <div className="relative">
              <input
                type="text"
                value={filtroPessoa}
                onChange={(e) => setFiltroPessoa(e.target.value)}
                className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                placeholder="Nome ou e-mail..."
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
          </div>
          <button
            type="submit"
            className="inline-flex items-center gap-2 px-4 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-all duration-200 font-medium"
          >
            <Search className="w-4 h-4" />
            Buscar
          </button>
        </form>
      </div>

      {/* Card: Resultado da busca por pessoa — pessoa e time em que pertence */}
      {temBuscaPorPessoa && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm overflow-hidden mb-6">
          <div className="p-6 border-b border-gray-200 flex items-center gap-3 bg-gray-50/80">
            <div className="w-10 h-10 bg-gradient-to-r from-brand-500 to-brand-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <Search className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Resultado da busca por pessoa</h3>
              <p className="text-sm text-gray-600 inline-flex items-center gap-1.5 mt-0.5">
                <User className="w-4 h-4 text-gray-500" />
                Pessoa
                <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                <Building2 className="w-4 h-4 text-gray-500" />
                Time em que pertence
                <span className="text-gray-500 font-medium ml-1">
                  ({resultadoBuscaPessoa.length} encontrada(s))
                </span>
              </p>
            </div>
          </div>
          <div className="overflow-x-auto">
            {resultadoBuscaPessoa.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                  <UserCircle className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-600 font-medium">Nenhuma pessoa encontrada</p>
                <p className="text-sm text-gray-500 mt-1">Tente outro nome ou e-mail.</p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <span className="inline-flex items-center gap-1.5">
                        <User className="w-4 h-4 text-gray-400" />
                        Nome
                      </span>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <span className="inline-flex items-center gap-1.5">
                        <Mail className="w-4 h-4 text-gray-400" />
                        E-mail
                      </span>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <span className="inline-flex items-center gap-1.5">
                        <Briefcase className="w-4 h-4 text-gray-400" />
                        Cargo
                      </span>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <span className="inline-flex items-center gap-1.5">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        Time
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {resultadoBuscaPessoa.map((pessoa) => (
                    <tr key={pessoa.idPessoa} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center gap-2 text-sm font-medium text-gray-900">
                          <span className="w-8 h-8 rounded-lg bg-brand-100 text-brand-600 flex items-center justify-center flex-shrink-0">
                            <User className="w-4 h-4" />
                          </span>
                          {pessoa.nome}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        <span className="inline-flex items-center gap-1.5">
                          {pessoa.email ? (
                            <>
                              <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              {pessoa.email}
                            </>
                          ) : (
                            '—'
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        <span className="inline-flex items-center gap-1.5">
                          {pessoa.cargoNome ? (
                            <>
                              <Briefcase className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              {pessoa.cargoNome}
                            </>
                          ) : (
                            '—'
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <CelulaTime timeId={pessoa.timeId} times={times} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Card: Pessoas no time (gerenciar por time) */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm overflow-hidden">
        <div className="p-6 border-b border-gray-200 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-brand-500 to-brand-600 rounded-lg flex items-center justify-center flex-shrink-0 min-w-[2.5rem] min-h-[2.5rem]">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">
                {timeSelecionado ? `Pessoas no time: ${timeSelecionado.descricao}` : 'Pessoas no time'}
              </h3>
              <p className="text-sm text-gray-600">
                {timeIdSelecionado == null
                  ? 'Selecione um time nos filtros para gerenciar membros'
                  : `${membrosExibidos.length} pessoa(s) no time`}
              </p>
            </div>
          </div>
          {timeIdSelecionado != null && (
            <button
              type="button"
              onClick={() => refetchNoTime()}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200"
              title="Atualizar"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          )}
        </div>

        {timeIdSelecionado == null ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-600 font-medium">Selecione um time nos filtros acima</p>
            <p className="text-sm text-gray-500 mt-1">Para listar membros, adicionar e remover pessoas do time.</p>
          </div>
        ) : loadingPessoasNoTime ? (
          <div className="flex justify-center py-12">
            <div className="loading-spinner w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Bloco Adicionar */}
            <div className="flex flex-wrap items-end gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="w-10 h-10 rounded-lg bg-brand-100 text-brand-600 flex items-center justify-center flex-shrink-0">
                  <UserPlus className="w-5 h-5" />
                </span>
                <div className="flex-1 min-w-[220px]">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Adicionar pessoa ao time</label>
                  <select
                    value={pessoaIdParaAdicionar}
                    onChange={(e) => setPessoaIdParaAdicionar(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  >
                    <option value="">Selecione uma pessoa</option>
                    {pessoasDisponiveisParaAdicionar.map((p) => (
                      <option key={p.idPessoa} value={p.idPessoa}>
                        {p.nome} {p.email ? `— ${p.email}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <button
                type="button"
                onClick={handleAdicionarAoTime}
                disabled={!pessoaIdParaAdicionar || updateMutation.isLoading}
                className="inline-flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-all font-medium shadow-theme-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <UserPlus className="w-4 h-4" />
                Adicionar
              </button>
            </div>

            {/* Tabela de membros */}
            <div>
              <h3 className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                <Users className="w-4 h-4 text-brand-500" />
                Membros do time
              </h3>
              {membrosExibidos.length === 0 ? (
                <div className="border border-gray-200 rounded-xl p-10 text-center bg-gray-50/50">
                  <UserCircle className="w-14 h-14 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium">Nenhuma pessoa neste time</p>
                  <p className="text-sm text-gray-500 mt-1">Use o bloco acima para adicionar pessoas.</p>
                </div>
              ) : (
                <div className="overflow-x-auto border border-gray-200 rounded-xl">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <span className="inline-flex items-center gap-1.5">
                            <User className="w-4 h-4 text-gray-400" />
                            Nome
                          </span>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <span className="inline-flex items-center gap-1.5">
                            <Mail className="w-4 h-4 text-gray-400" />
                            E-mail
                          </span>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <span className="inline-flex items-center gap-1.5">
                            <Briefcase className="w-4 h-4 text-gray-400" />
                            Cargo
                          </span>
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <span className="inline-flex items-center gap-1.5 justify-end">
                            <UserMinus className="w-4 h-4 text-gray-400" />
                            Ações
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {membrosExibidos.map((pessoa) => (
                        <tr key={pessoa.idPessoa} className="hover:bg-gray-50/50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center gap-2 text-sm font-medium text-gray-900">
                              <span className="w-8 h-8 rounded-lg bg-brand-100 text-brand-600 flex items-center justify-center flex-shrink-0">
                                <User className="w-4 h-4" />
                              </span>
                              {pessoa.nome}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            <span className="inline-flex items-center gap-1.5">
                              {pessoa.email ? (
                                <>
                                  <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                  {pessoa.email}
                                </>
                              ) : (
                                '—'
                              )}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            <span className="inline-flex items-center gap-1.5">
                              {pessoa.cargoNome ? (
                                <>
                                  <Briefcase className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                  {pessoa.cargoNome}
                                </>
                              ) : (
                                '—'
                              )}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <button
                              type="button"
                              onClick={() => handleRemoverDoTime(pessoa)}
                              disabled={updateMutation.isLoading}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-all disabled:opacity-50"
                              title="Remover do time"
                            >
                              <UserMinus className="w-4 h-4" />
                              Remover
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
